import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";
import "../styles/Dashboard.css";

const TZ = "America/Bogota";
const UMBRAL_BEBIDAS = 5;

const dinero = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;
const numero = (n) => Number(n || 0).toLocaleString("es-CO");

// Fecha civil en Bogotá, desplazada `dias` respecto de hoy.
function fechaBogota(dias = 0) {
  const hoy = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  const [y, m, d] = hoy.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + dias));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
}

/* --------------------------------------------------------------- piezas ---- */

function Cifra({ etiqueta, valor, apoyo, variacion }) {
  return (
    <div className="cifra-tarjeta">
      <span className="cifra-etiqueta">{etiqueta}</span>
      <strong className="cifra-valor">{valor}</strong>
      <span className="cifra-apoyo">
        {variacion && (
          <span className={`variacion ${variacion.signo}`}>
            {variacion.signo === "sube" ? "▲" : variacion.signo === "baja" ? "▼" : "—"} {variacion.texto}
          </span>
        )}
        {variacion && apoyo ? " · " : ""}
        {apoyo}
      </span>
    </div>
  );
}

// Un número solo no dice si va bien o mal: siempre va contra una referencia.
function compararConAyer(hoy, ayer) {
  if (ayer === 0) return hoy > 0 ? { signo: "sube", texto: "ayer sin ventas" } : null;
  const pct = ((hoy - ayer) / ayer) * 100;
  if (Math.abs(pct) < 0.5) return { signo: "igual", texto: "igual que ayer" };
  return {
    signo: pct > 0 ? "sube" : "baja",
    texto: `${pct > 0 ? "+" : "−"}${Math.abs(pct).toFixed(0)}% vs ayer`,
  };
}

function Marco({ activo }) {
  if (!activo) return null;
  return <div className="cargando-velo" aria-hidden="true" />;
}

/* -------------------------------------------------------------- pantalla ---- */

const Dashboard = () => {
  const apiUrl = process.env.REACT_APP_API_URL + "/api";

  const [porHora, setPorHora] = useState([]);
  const [hoy, setHoy] = useState(null);
  const [ayer, setAyer] = useState(null);
  const [semana, setSemana] = useState(null);
  const [ingredientesBajos, setIngredientesBajos] = useState([]);
  const [bebidasBajas, setBebidasBajas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [rHora, rHoy, rAyer, rSemana, rIng, rBeb] = await Promise.all([
        axios.get(`${apiUrl}/dashboard/ventas/hoy`),
        axios.get(`${apiUrl}/reportes/total-sales?period=dia`),
        axios.get(`${apiUrl}/reportes/total-sales?period=dia&fecha=${fechaBogota(-1)}`),
        axios.get(`${apiUrl}/reportes/total-sales?period=semana`),
        axios.get(`${apiUrl}/ingredientes`),
        axios.get(`${apiUrl}/bebidas`),
      ]);

      setPorHora(
        (rHora.data || []).map((h) => ({
          hora: h.hora_exacta,
          monto: Number(h.total_ventas) || 0,
          ventas: Number(h.cantidad_ventas) || 0,
        }))
      );
      setHoy(rHoy.data?.totales ?? { monto: 0, ventas: 0 });
      setAyer(rAyer.data?.totales ?? { monto: 0, ventas: 0 });
      setSemana(rSemana.data?.totales ?? { monto: 0, ventas: 0 });

      setIngredientesBajos(
        (rIng.data || []).filter(
          (i) => parseFloat(i.stock_current) < parseFloat(i.stock_minimum)
        )
      );
      setBebidasBajas((rBeb.data || []).filter((b) => parseFloat(b.stock) < UMBRAL_BEBIDAS));
    } catch (e) {
      setError("No se pudieron cargar los datos del tablero.");
    } finally {
      setCargando(false);
    }
  }, [apiUrl]);

  useEffect(() => { cargar(); }, [cargar]);

  const montoHoy = hoy?.monto ?? 0;
  const ventasHoy = hoy?.ventas ?? 0;
  const promedio = ventasHoy > 0 ? Math.round(montoHoy / ventasHoy) : 0;
  const variacion = hoy && ayer ? compararConAyer(montoHoy, ayer.monto) : null;

  const maxHora = useMemo(
    () => porHora.reduce((m, h) => Math.max(m, h.monto), 0),
    [porHora]
  );
  const horaPico = useMemo(
    () => (maxHora > 0 ? porHora.find((h) => h.monto === maxHora) : null),
    [porHora, maxHora]
  );

  const alertas = ingredientesBajos.length + bebidasBajas.length;

  return (
    <div className="tablero">
      <div className="encabezado-pagina">
        <div>
          <h1>Tablero</h1>
          <p className="subtitulo">
            {new Intl.DateTimeFormat("es-CO", {
              timeZone: TZ, weekday: "long", day: "numeric", month: "long",
            }).format(new Date())}
          </p>
        </div>
        <button className="secundario" onClick={cargar} disabled={cargando}>
          {cargando ? "Actualizando…" : "Actualizar"}
        </button>
      </div>

      {error && <p className="aviso-bloque error" role="alert">{error}</p>}

      <div className={`tablero-cuerpo ${cargando ? "ocupado" : ""}`}>
        <Marco activo={cargando} />

        {/* Las cifras: una principal y tres de contexto. */}
        <div className="cifras">
          <div className="cifra-tarjeta principal">
            <span className="cifra-etiqueta">Ventas de hoy</span>
            <strong className="cifra-valor">{dinero(montoHoy)}</strong>
            <span className="cifra-apoyo">
              {variacion && (
                <span className={`variacion ${variacion.signo}`}>
                  {variacion.signo === "sube" ? "▲" : variacion.signo === "baja" ? "▼" : "—"} {variacion.texto}
                </span>
              )}
            </span>
          </div>
          <Cifra etiqueta="Transacciones" valor={numero(ventasHoy)}
            apoyo={ayer ? `${numero(ayer.ventas)} ayer` : ""} />
          <Cifra etiqueta="Ticket promedio" valor={dinero(promedio)}
            apoyo={ventasHoy === 0 ? "sin ventas aún" : ""} />
          <Cifra etiqueta="Esta semana" valor={dinero(semana?.monto ?? 0)}
            apoyo={semana ? `${numero(semana.ventas)} ventas` : ""} />
        </div>

        {/* Serie por hora */}
        <section className="tarjeta-base">
          <div className="encabezado-seccion">
            <h2>Ventas por hora</h2>
            <span className="texto-auxiliar">
              {horaPico ? `Pico a las ${horaPico.hora} · ${dinero(maxHora)}` : "Sin ventas todavía"}
            </span>
          </div>

          {maxHora === 0 ? (
            <div className="estado-vacio">
              <h3>Todavía no hay ventas hoy</h3>
              <p>La curva del día aparecerá aquí en cuanto se registre la primera.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={porHora} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#E6E0D2" />
                <XAxis dataKey="hora" tick={{ fontSize: 11, fill: "#8A968C" }}
                  tickLine={false} axisLine={{ stroke: "#E6E0D2" }} interval={1} />
                <YAxis tick={{ fontSize: 11, fill: "#8A968C" }} tickLine={false} axisLine={false}
                  width={64} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                <Tooltip
                  cursor={{ fill: "rgba(46,107,68,0.07)" }}
                  contentStyle={{
                    borderRadius: 12, border: "1px solid #E6E0D2",
                    boxShadow: "0 4px 12px rgba(31,42,34,.1)", fontSize: 13,
                  }}
                  formatter={(v, _n, p) => [
                    `${dinero(v)} · ${p.payload.ventas} ${p.payload.ventas === 1 ? "venta" : "ventas"}`,
                    "",
                  ]}
                  labelFormatter={(h) => `${h}`}
                />
                <Bar dataKey="monto" radius={[4, 4, 0, 0]} maxBarSize={22}>
                  {porHora.map((h) => (
                    // La hora pico se distingue sola; el resto es una sola serie.
                    <Cell key={h.hora} fill={h.monto === maxHora ? "#1B4229" : "#2E6B44"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </section>

        {/* Inventario */}
        <section className="tarjeta-base">
          <div className="encabezado-seccion">
            <h2>Alertas de inventario</h2>
            {alertas > 0
              ? <span className="distintivo aviso">{alertas} {alertas === 1 ? "alerta" : "alertas"}</span>
              : <span className="distintivo exito">Todo en orden</span>}
          </div>

          {alertas === 0 ? (
            <div className="estado-vacio">
              <h3>Sin faltantes</h3>
              <p>Ningún ingrediente está por debajo de su mínimo y no hay bebidas escasas.</p>
            </div>
          ) : (
            <div className="alertas">
              {ingredientesBajos.length > 0 && (
                <div className="grupo-alerta">
                  <h3>Ingredientes bajo el mínimo</h3>
                  <div className="tabla-scroll">
                    <table>
                      <thead>
                        <tr><th>Ingrediente</th><th className="cifra">Actual</th><th className="cifra">Mínimo</th><th>Estado</th></tr>
                      </thead>
                      <tbody>
                        {ingredientesBajos.map((i) => {
                          const agotado = parseFloat(i.stock_current) <= 0;
                          return (
                            <tr key={i.ingredient_id}>
                              <td>{i.name}</td>
                              <td className="cifra">{numero(i.stock_current)}</td>
                              <td className="cifra">{numero(i.stock_minimum)}</td>
                              <td>
                                <span className={`distintivo ${agotado ? "peligro" : "aviso"}`}>
                                  {agotado ? "Agotado" : "Bajo"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {bebidasBajas.length > 0 && (
                <div className="grupo-alerta">
                  <h3>Bebidas escasas <span className="texto-auxiliar">(menos de {UMBRAL_BEBIDAS})</span></h3>
                  <div className="tabla-scroll">
                    <table>
                      <thead>
                        <tr><th>Bebida</th><th className="cifra">Existencias</th><th>Estado</th></tr>
                      </thead>
                      <tbody>
                        {bebidasBajas.map((b) => {
                          const agotado = parseFloat(b.stock) <= 0;
                          return (
                            <tr key={b.bebida_id}>
                              <td>{b.name}</td>
                              <td className="cifra">{numero(b.stock)}</td>
                              <td>
                                <span className={`distintivo ${agotado ? "peligro" : "aviso"}`}>
                                  {agotado ? "Agotada" : "Baja"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
